// Copyright (c) 2011-2022 by Curalate, Inc.

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  getReadableDateRangeString,
  getReadableDateString,
} from '@components/exportable-report/ExportableReportHelpers';
import {
  BazaarvoiceLogo,
  Rectangle,
  ReportCoverContainer,
} from '@components/reports/ReportSharedUI';
import { getSessionDateRange } from '@instagram-account/redux/selectors/getSessionDateRange';

interface Props {
  className?: string;
  clientName: string;
  creationDateText?: string;
  dateRangeText?: string;
  reportTitle: string;
}
export const ReportCover: React.FC<Props> = ({
  className = '',
  clientName,
  creationDateText,
  dateRangeText,
  reportTitle,
}) => {
  const creationDate = creationDateText ?? getReadableDateString(new Date());

  // The ScreenCaptureService lambda stores the creator name in local storage at runtime
  //   prior to the ReportCover loading inside the Lambda Chrome. We can't simply use the
  //   existing user, because inside the Lambda, it's always the screen capture service global user.
  const creatorName = sessionStorage.getItem('ReportCreatorName');

  const sessionDateRange = useSelector(getSessionDateRange);
  const dateRangeString =
    dateRangeText ?? getReadableDateRangeString(sessionDateRange);

  return (
    <ReportCoverContainer className={className}>
      <BazaarvoiceLogo />
      <div>
        <ClientName>{clientName}</ClientName>
        <Rectangle />
        <ReportTitle>{reportTitle}</ReportTitle>
        <DateString>{dateRangeString}</DateString>
      </div>
      <CreationDetails>
        Created {creatorName && `by ${creatorName} `} on {creationDate}
      </CreationDetails>
    </ReportCoverContainer>
  );
};

const ClientName = styled.div(
  ({ theme }) => `
  font-size: ${theme.fontSizes[0]};
  font-weight: ${theme.fontWeights.light};
  line-height: ${theme.lineHeights.titleLeading};
  color: ${theme.colors.white};
  `,
);

const ReportTitle = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[2]};
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes[3]};
  font-weight: ${theme.fontWeights.semiBold};
  line-height: ${theme.lineHeights.bodyLeading};
  `,
);

const DateString = styled.div(
  ({ theme }) => `
  font-size: ${theme.fontSizes[3]};
  font-weight: ${theme.fontWeights.normal};
  line-height: ${theme.lineHeights.bodyLeading};
  color: ${theme.colors.white};
`,
);

const CreationDetails = styled.div(
  ({ theme }) => `
  font-size: ${theme.fontSizes[5]};
  font-weight: ${theme.fontWeights.normal};
  line-height: ${theme.lineHeights.uiLeading};
  color: ${theme.colors.white};
`,
);
